import React, { ChangeEvent, useRef } from "react";

import * as S from "@styled";
import { /*importIcon,*/ exportIcon } from "shared/icons/svgHelpers";
import DropdownButton from "shared/components/DropdownButton";
import { handleToggleDropdown, handleHideDropdown } from "shared/styles/helpers";
import { Utilities } from "shared/utilities";

import { AppContext } from "../App";
import { ProcessTemplateAppReducerState } from "processTemplates/model/AppState";
import {
    setDeleteTemplateIds, copyProcessTemplates, exportProcessTemplates, setTaggedTemplateIds, handleApiError,
    PROCESS_TEMPLATE_BASE_URL, setLoading, getProcessTemplates, saveProcessTemplatesAsCsv, saveAllProcessTemplatesAsCsv,
    pinProcessTemplates, setMissingTasks
} from "processTemplates/actions/AppActions";
import { MissingTask } from "processTemplates/model/MissingTask";
import { Permissions } from "shared/model/AllPermissions";

interface IActionButtonsProps {
    selectedTemplateIds: number[];
}

const ActionButtons: React.FC<IActionButtonsProps> = ({ selectedTemplateIds }) => {
    const { state, dispatch } = React.useContext<ProcessTemplateAppReducerState>(AppContext);
    const templateImportFileInputRef = useRef<HTMLInputElement>(null);

    // const handleOpenTemplateImportModalButtonClick = () => {
    //     setImportModalOpen(dispatch, true);
    // };

    const handleTemplateImportFromFileButtonClick = () => {
        templateImportFileInputRef.current!.value = ""; // Clear the possible previous selection
        templateImportFileInputRef.current!.disabled = false;
        templateImportFileInputRef.current!.click();
    };

    const handleSaveTemplatesCsvButtonClick = () => {
        saveAllProcessTemplatesAsCsv(dispatch);
    };

    const handleTemplateImportFileInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
        templateImportFileInputRef.current!.disabled = true;

        const files = event.currentTarget.files;
        if (!files?.length) {
            return;
        }

        const formData = new FormData();
        formData.append("file", files[0]);

        try {
            setLoading(dispatch, true);
            await Utilities.postForm(`${PROCESS_TEMPLATE_BASE_URL}/importFromFile`, formData)
                .then(Utilities.parseJsonResponse)
                .then((missingTasks: MissingTask[]) => {
                    if (missingTasks.length === 0) {
                        Utilities.toastSuccess("Process Template was successfully imported");
                        getProcessTemplates(dispatch);
                    } else {
                        setMissingTasks(dispatch, missingTasks);
                    }
                });
        } catch (e) {
            handleApiError(dispatch, e);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <S.ButtonGroup gap={20}>
            {/* <S.Button
                onClick={handleOpenTemplateImportModalButtonClick}
                id="open-template-import-modal-button"
            >
                {getIconSvgWrapper(importIcon)}
                Template import
            </S.Button> */}

            {state.userPermissions[Permissions.ProcessTemplateEdit] && (
                <>
                    <S.Button
                        onClick={handleTemplateImportFromFileButtonClick}
                        id="template-import-from-file-button"
                    >
                        <S.ButtonIcon className="ci-folder_open" />
                        Import from file
                    </S.Button>

                    <S.SROnly>
                        <form id="import-template-from-file-form">
                            <label>
                                Select template file
                            </label>
                            <input
                                ref={templateImportFileInputRef}
                                id="import-template-file-input"
                                type="file"
                                onChange={handleTemplateImportFileInputChange}
                                disabled={true}
                            />
                        </form>
                    </S.SROnly>
                </>
            )}

            {state.userPermissions[Permissions.ProcessTemplateView] && (
                <S.Button
                    onClick={handleSaveTemplatesCsvButtonClick}
                    id="save-templates-csv-button"
                >
                    {getIconSvgWrapper(exportIcon)}
                    Save all as .csv
                </S.Button>
            )}

            {selectedTemplateIds.length > 0 && (
                <ActionsMenu selectedTemplateIds={selectedTemplateIds} />
            )}
        </S.ButtonGroup>
    );
};

export default ActionButtons;

const getIconSvgWrapper = (svg: string) => (
    <span
        dangerouslySetInnerHTML={{ __html: svg }}
        style={{ height: '20px', marginRight: '10px', marginLeft: '-5px' }}
    />
);

const ActionsMenu: React.FC<IActionButtonsProps> = ({ selectedTemplateIds }) => {
    const { state, dispatch } = React.useContext<ProcessTemplateAppReducerState>(AppContext);

    const actionMenuId = 'template-action-menu';

    const handleOpenActionMenuButtonClick = () => handleToggleDropdown(actionMenuId);

    const getActionClickHandler = (clickHandler: () => void) => {
        return () => {
            handleHideDropdown(actionMenuId);
            clickHandler();
        };
    };

    const handleCopyButtonClick = getActionClickHandler(() => {
        copyProcessTemplates(dispatch, selectedTemplateIds);
    });

    const handleDeleteButtonClick = getActionClickHandler(() => {
        setDeleteTemplateIds(dispatch, selectedTemplateIds);
    });

    const handleExportToFileClick = getActionClickHandler(() => {
        exportProcessTemplates(dispatch, selectedTemplateIds);
    });

    const handleSaveAsCsvButtonClick = getActionClickHandler(() => {
        saveProcessTemplatesAsCsv(dispatch, selectedTemplateIds);
    });

    const handleTagButtonClick = getActionClickHandler(() => {
        setTaggedTemplateIds(dispatch, selectedTemplateIds);
    });

    const handlePinButtonClick = getActionClickHandler(() => {
        const templateGuids = state.processTemplates!.filter(template => selectedTemplateIds.includes(template.id)).map((x) => x.guid);
        pinProcessTemplates(dispatch, templateGuids);
    });

    return (
        <DropdownButton
            id={actionMenuId}
            buttonComponent={
                <S.Button onClick={handleOpenActionMenuButtonClick}>
                    {FRENDS.Localizations.Actions}
                    &nbsp;
                    <i className="ci-chevron_big_down" />
                </S.Button>
            }
            dropDownButtonId={"template-open-action-menu-button"}
            dropDownButtonClassName={"row-actions-menu-button-selector"}
            dropDownContentPullRight={true}
        >
            <button
                id="template-action-menu-copy-button"
                onClick={handleCopyButtonClick}
            >
                Copy
            </button>
            <button
                id="template-action-menu-delete-button"
                onClick={handleDeleteButtonClick}
            >
                Delete
            </button>
            <button
                id="template-action-menu-export-to-file-button"
                onClick={handleExportToFileClick}
            >
                Export to file
            </button>
            <button
                id="template-action-menu-save-as-csv"
                onClick={handleSaveAsCsvButtonClick}
            >
                Save as .csv
            </button>
            <button
                id="template-action-menu-tag-button"
                onClick={handleTagButtonClick}
            >
                Tag
            </button>
            <button
                id="template-action-menu-pin-button"
                onClick={handlePinButtonClick}
            >
                Pin
            </button>
        </DropdownButton>
    );
};